import Axios from 'axios';
import * as Auth from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import mainApi from 'api/main';
import { notificationCreated } from 'reducers/appNotification';
import { CognitoErrors } from 'utils/constants';
import { is400StatusHandled } from 'utils/helpers';

// Set once the session is known to be gone (401 or no Cognito tokens) so that the
// dozens of requests a page fires in parallel trigger a single sign-out, not one each.
let sessionExpiryHandled = false;

export const configureAxios = store => {
    const { dispatch } = store;

    // Allow the expiry flow to run again after the next successful sign-in / refresh.
    Hub.listen('auth', ({ payload: { event } }) => {
        if (event === 'signedIn' || event === 'tokenRefresh') {
            sessionExpiryHandled = false;
        }
    });

    // Navigating to /sign-in alone is not enough: the user is still logged in in the store,
    // so the router sends them straight back to the page, which re-fires every request
    // and gets 401 again. Signing out clears the store, so /sign-in actually sticks.
    const handleExpiredSession = () => {
        if (sessionExpiryHandled) return;
        sessionExpiryHandled = true;

        dispatch(notificationCreated('ERROR_401'));

        // If sign-out failed (or rejected), let a later request try again instead of staying stuck.
        const allowRetryIfStillSignedIn = () => {
            if (store.getState().user?.internalUser) {
                sessionExpiryHandled = false;
            }
        };

        Promise.resolve(dispatch(mainApi.endpoints.signOut.initiate(true))).then(
            allowRetryIfStillSignedIn,
            allowRetryIfStillSignedIn
        );
    };

    Axios.defaults.baseURL = process.env.REACT_APP_API_URL;

    Axios.interceptors.request.use(
        async opt => {
            const { user } = store.getState();
            const { baseURL } = Axios.defaults;
            const requestFullURL = baseURL + opt.url;
            const request = {
                ...opt,
                baseURL: opt.url.includes('/translations')
                    ? requestFullURL.replace('/api/', '/').replace(opt.url, '')
                    : baseURL,
                responseType: 'json',
                headers:
                    opt.method === 'patch'
                        ? { 'Content-Type': 'application/merge-patch+json' }
                        : {},
            };

            try {
                const { tokens } = await Auth.fetchAuthSession();

                if (tokens?.accessToken) {
                    let stringToken = tokens.accessToken.toString();
                    request.headers.Authorization = `Bearer ${stringToken}`;
                } else {
                    throw new Error(CognitoErrors.NO_USER);
                }

                if (opt.headers['Content-Type']) {
                    request.headers['Content-Type'] = opt.headers['Content-Type'];
                }

                if (opt.responseType) {
                    request.responseType = opt.responseType;
                }

                return request;
            } catch (error) {
                const noUser =
                    error?.message === CognitoErrors.NO_USER || error === CognitoErrors.NO_USER;

                if (noUser && !request.url.includes('logout')) {
                    // /setup-password route is an exception to this rule
                    if (user.internalUser) {
                        // The app thinks we're signed in but Cognito has no session: don't send
                        // a request that can only come back 401, sign out once instead.
                        handleExpiredSession();
                        throw new Axios.Cancel('Session expired.');
                    }

                    if (request.url.includes('/me')) throw new Axios.Cancel('No user.');
                }

                return request;
            }
        },
        error => Promise.reject(error)
    );

    Axios.interceptors.response.use(
        response => {
            if (
                response?.config?.url?.includes('download') ||
                response?.config?.url?.includes('/payments/pec') ||
                response?.config?.url?.includes('/simulate') ||
                response?.config?.url?.includes('/export-list') ||
                response?.config?.url?.includes('/export') ||
                (response?.config?.url?.includes('/report') &&
                    !response?.config?.url?.includes('/reporting')) ||
                response?.config?.url?.includes('/portfolio/generate') ||
                (response?.config?.method === 'get' &&
                    (response?.config?.url?.includes('/notifications') ||
                        response?.config?.url?.includes('/referrals') ||
                        response?.config?.url?.includes('/retrocessions') ||
                        response?.config?.url?.includes('/sgps')))
            ) {
                return Promise.resolve(response);
            }
            return Promise.resolve(response.data);
        },
        error => {
            const { user } = store.getState();

            // Cancelled on purpose (session expired, component unmounted): not a network error.
            if (Axios.isCancel(error)) {
                return Promise.reject(error);
            }

            if (!error.response) {
                // Network error
                dispatch(notificationCreated('ERROR_GENERAL'));
                return Promise.reject(error);
            }

            switch (error.response?.status) {
                case 400:
                    if (!is400StatusHandled(error.response)) {
                        dispatch(notificationCreated('ERROR_GENERAL'));
                    }
                    return Promise.reject(error.response);
                case 401:
                    if (user?.internalUser) {
                        handleExpiredSession();
                    }
                    return Promise.reject(error.response.data);
                case 403:
                    if (!user?.internalUser?.impersonatedByUser) {
                        dispatch(notificationCreated('ERROR_GENERAL'));
                    }
                    return Promise.reject(error.response.status);
                case 404:
                    if (error?.config?.url?.includes('/documents/download')) {
                        return Promise.reject(error.response);
                    }
                    return Promise.reject(error.response.data);
                case 415:
                    return Promise.reject(error.response);
                case 422:
                    return Promise.reject(error.response);
                case 500:
                    dispatch(notificationCreated('ERROR_GENERAL'));
                    return Promise.reject(error);
                default:
                    return Promise.reject(error.response);
            }
        }
    );
};
